import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AccountState } from '../state';
import { AccountCommand, RulesSubmitCommand, RulesDeleteCommand } from '../commands';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
/**
 * Rules Submit Policy
 *
 * Responsibilities:
 * - React to rules/submit command (add/edit rules)
 * - React to rules/delete command (remove rules)
 * - Execute backend commands (SetRule, RemoveRule)
 * - Update local state optimistically
 * - Handle errors
 */
export const createRulesSubmitPolicy = (
  backend: ClientLibraryType,
  accountId: string
): Policy<AccountState, AccountCommand> => ({
  name: 'rules-submit',

  shouldReact: ( prev, next ) => {
    // React when submitting goes from false to true
    return (
      !prev.rules.loading.submitting &&
      next.rules.loading.submitting
    );
  },

  onStateChange: async ({ next, ctx }) => {
    // This policy is triggered by the loading state change
    // The actual command payload will be passed through a different mechanism
    // For now, we'll reset the loading state
    // The actual rule operations are handled by direct dispatch from the component

    ctx.dispatch(
      { type: 'account/rules/loading/setSubmitting', payload: { isLoading: false }},
      { source: 'api' }
    );
  }
});

/**
 * Helper function to add a rule via backend
 */
export const addRule = async (
  backend: ClientLibraryType,
  accountId: string,
  rule: any,
  dispatch: any
): Promise<void> => {
  dispatch(
    { type: 'account/rules/loading/setSubmitting', payload: { isLoading: true }},
    { source: 'api' }
  );

  try {
    const { id, name, declaredInputs, hierarchy, ...ruleWithoutIds } = rule;
    const newRule = {
      ...ruleWithoutIds,
      hierarchyLevel: 'account',
      hierarchyEntityIds: [accountId]
    };

    const result = await backend.commands.SetRule({ id: accountId, body: newRule });
    let rulePersisted = result.events.find(( e: any ) => e.name === 'RuleSet' )?.data as any;

    if ( rulePersisted ) {
      rulePersisted = {
        ...rulePersisted,
        name,
        declaredInputs,
        hierarchy,
        id: rulePersisted.ruleId
      };

      dispatch(
        { type: 'account/rules/applied/add', payload: { rule: rulePersisted }},
        { source: 'api' }
      );
    }
  } catch ( error ) {
    console.error( '[account] Rule add error:', error );
    throw error;
  } finally {
    dispatch(
      { type: 'account/rules/loading/setSubmitting', payload: { isLoading: false }},
      { source: 'api' }
    );
  }
};

/**
 * Helper function to edit a rule via backend
 */
export const editRule = async (
  backend: ClientLibraryType,
  accountId: string,
  ruleId: string,
  rule: any,
  dispatch: any
): Promise<void> => {
  dispatch(
    { type: 'account/rules/loading/setSubmitting', payload: { isLoading: true }},
    { source: 'api' }
  );

  try {
    const { id, name, declaredInputs, ruleDefinition, accountId: ruleAccountId, hierarchy, possibleTriggeringEvents, ...ruleWithoutIds } = rule;
    const updatedRule = {
      ...ruleWithoutIds,
      ruleId: id,
      hierarchyLevel: 'account',
      hierarchyEntityIds: ( !rule.hierarchyEntityIds || rule.hierarchyEntityIds.length === 0 )
        ? [accountId]
        : rule.hierarchyEntityIds
    };

    await backend.commands.SetRule({ id: accountId, body: updatedRule });

    // Update local state
    dispatch(
      {
        type: 'account/rules/applied/update',
        payload: {
          ruleId,
          rule: {
            ...rule,
            hierarchyEntityIds: ( !rule.hierarchyEntityIds || rule.hierarchyEntityIds.length === 0 )
              ? [accountId]
              : rule.hierarchyEntityIds
          }
        }
      },
      { source: 'api' }
    );
  } catch ( error ) {
    console.error( '[account] Rule edit error:', error );
    throw error;
  } finally {
    dispatch(
      { type: 'account/rules/loading/setSubmitting', payload: { isLoading: false }},
      { source: 'api' }
    );
  }
};

/**
 * Helper function to delete a rule via backend
 */
export const deleteRule = async (
  backend: ClientLibraryType,
  accountId: string,
  ruleId: string,
  dispatch: any
): Promise<void> => {
  dispatch(
    { type: 'account/rules/loading/setSubmitting', payload: { isLoading: true }},
    { source: 'api' }
  );

  try {
    await backend.commands.RemoveRule({ id: accountId, body: { ruleId }});

    // Update local state
    dispatch(
      { type: 'account/rules/applied/remove', payload: { ruleId }},
      { source: 'api' }
    );
  } catch ( error ) {
    console.error( '[account] Rule delete error:', error );
    throw error;
  } finally {
    dispatch(
      { type: 'account/rules/loading/setSubmitting', payload: { isLoading: false }},
      { source: 'api' }
    );
  }
};
